import React from 'react';

import { FlexRow } from '@editor/components/utilities/layouts';
import { Button } from '@repo/ui/components/Buttons';
import CrownIcon from '@repo/ui/components/Icon/CrownIcon';
import PremiumBadgeIcon from '@repo/ui/components/Icon/PremiumBadgeIcon';
import PricingCheckIcon from '@repo/ui/components/Icon/PricingCheckIcon';
import { Spacer } from '@repo/ui/components/Spacer';
import { H3, P2 } from '@repo/ui/components/Typography';
import { themeStyle, useNewThemeActive } from '@repo/ui/theming';

import usePricingPlansData from '../../hooks/usePricingPlansData';
import { getAddonFromSubscription } from '../../utils';
import { ResponsiveHeader, ResponsiveText } from '../styles';

import {
  Content,
  BackgroundImageWrapper,
  FeaturesList,
  Feature,
} from './styles';
import { BenefitsOverviewProps } from './types';

export const BenefitsOverview: React.FC<BenefitsOverviewProps> = ({
  onNextClick,
  plan,
  trialEndDate,
  subscription,
}) => {
  const planConfig = usePricingPlansData({
    addon: getAddonFromSubscription(subscription),
  })[plan];

  const isNewThemeActive = useNewThemeActive();

  return (
    <>
      <ResponsiveHeader>
        You are about to cancel your {planConfig.title} plan trial
      </ResponsiveHeader>
      <Spacer height="16px" />
      <ResponsiveText>
        You will still have access to the features included in the{' '}
        {planConfig.title} plan until {trialEndDate}.
      </ResponsiveText>
      <Spacer height="24px" />
      <BackgroundImageWrapper>
        <Content>
          <FlexRow alignItems="center" gap="8px">
            <PremiumBadgeIcon height="20px" />
            <H3 color={themeStyle.inkMain}>{planConfig.title} plan</H3>
          </FlexRow>
          <FeaturesList>
            {planConfig.mainFeatures.map((feature) => (
              <Feature key={feature.label}>
                <PricingCheckIcon
                  height="18px"
                  theme={{
                    color: isNewThemeActive
                      ? themeStyle.varBlueMedium
                      : themeStyle.varBrandMedium,
                  }}
                />
                <P2 color={themeStyle.inkMain}>
                  {feature.bold ? <b>{feature.label}</b> : feature.label}
                </P2>
                {feature.premium && <CrownIcon height="10px" />}
              </Feature>
            ))}
            {planConfig?.otherFeatures?.map((feature) => (
              <Feature key={feature.label}>
                <PricingCheckIcon
                  height="18px"
                  theme={{
                    color: isNewThemeActive
                      ? themeStyle.varBlueMedium
                      : themeStyle.varBrandMedium,
                  }}
                />
                <P2 color={themeStyle.inkMain}>
                  {feature.bold ? <b>{feature.label}</b> : feature.label}
                </P2>
                {feature.premium && <CrownIcon height="10px" />}
              </Feature>
            ))}
          </FeaturesList>
        </Content>
      </BackgroundImageWrapper>
      <Spacer height="24px" />
      <Button
        height="40px"
        label="Continue Cancellation"
        name="continueCancellationButton"
        style={{ flexShrink: 0 }}
        width="100%"
        onClick={onNextClick}
      />
    </>
  );
};
